import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  Image,
  Text,
  VStack,
  Badge,
  ScaleFade,
  HStack,
  useToast,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter
} from '@chakra-ui/react';
import { COLORS, GRADIENTS, PAYMENT_AMOUNTS, ROUTES } from '../utils/constants';
import useAuth from '../store/useAuth';
import useCredits from '../store/useCredits';
import { FaImage, FaVideo, FaRobot } from 'react-icons/fa';
import { Icon } from '@chakra-ui/react';

// Credit package component
const CreditPackage = ({ amount, price, bonusPercentage, label, onSelect, isSelected }) => {
  const bonusCredits = Math.floor(amount * (bonusPercentage / 100));
  
  return (
    <Box
      bg={isSelected ? "rgba(0, 168, 107, 0.15)" : "rgba(0, 0, 0, 0.6)"}
      borderWidth="1px"
      borderColor={isSelected ? COLORS.accent : COLORS.primary}
      borderRadius="lg"
      overflow="hidden"
      p={5}
      position="relative"
      transition="all 0.3s"
      className={isSelected ? "glow-border enhanced-glow" : "glow-border"}
      cursor="pointer"
      onClick={onSelect}
      _hover={{
        borderColor: COLORS.accent,
        transform: 'translateY(-5px)',
        boxShadow: '0 10px 25px rgba(0, 168, 107, 0.3)'
      }}
    >
      {bonusPercentage > 0 && (
        <Badge
          position="absolute"
          top="-10px"
          right="-10px"
          bg={COLORS.accent}
          color="black"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="full"
          boxShadow="0 0 10px rgba(50, 205, 50, 0.5)"
        >
          +{bonusPercentage}% BONUS
        </Badge>
      )}
      
      <VStack spacing={3} align="center">
        <Text fontSize="xl" fontWeight="bold" color={COLORS.accent} textTransform="uppercase">
          {label}
        </Text>
        
        <Text fontSize="2xl" fontWeight="bold" color={COLORS.text}>
          {amount.toLocaleString()} Credits
        </Text>
        
        {bonusCredits > 0 && (
          <Text fontSize="md" color={COLORS.accent}>
            +{bonusCredits.toLocaleString()} Bonus Credits
          </Text>
        )}
        
        <Text fontSize="lg" fontWeight="bold" color={COLORS.accent}>
          {price.toLocaleString()} XGCL
        </Text>
        
        <Text fontSize="sm" color={COLORS.textSecondary}>
          {((amount + bonusCredits) / price).toFixed(3)} credits per XGCL
        </Text>
      </VStack>
    </Box>
  );
};

function BuyCredits() {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [xamanPaymentData, setXamanPaymentData] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const navigate = useNavigate();
  const toast = useToast();
  
  const { user, isLoggedIn } = useAuth();
  const { balance, addCredits } = useCredits();
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  // Credit packages
  const creditPackages = [
    { id: 'start', amount: 800, price: 1109, bonusPercentage: 0, label: 'Start' },
    { id: 'pro', amount: 2500, price: 2772, bonusPercentage: 20, label: 'Pro' },
    { id: 'saver', amount: 6000, price: 5987, bonusPercentage: 28, label: 'Saver' },
  ];
  
  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
  };
  
  const getTotalCredits = () => {
    if (selectedPackage) {
      const bonusCredits = Math.floor(selectedPackage.amount * (selectedPackage.bonusPercentage / 100));
      return selectedPackage.amount + bonusCredits;
    }
    return 0;
  };
  
  const getXgcPrice = () => {
    if (selectedPackage) {
      return selectedPackage.price;
    }
    return 0;
  };
  
  const handlePurchase = async () => {
    if (!selectedPackage) {
      toast({
        title: 'No package selected',
        description: 'Please select a credit package',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Start payment process
    setIsProcessing(true);
    
    try {
      // Simulate API call to initiate XAMAN payment
      // In a real app, this would call your backend which would use XAMAN SDK
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock XAMAN payment data
      const mockPaymentData = {
        xamanPaymentUrl: 'https://xaman.app/sign/123456789',
        qrCode: 'data:image/png;base64,iVBORw0KGg...', // In real app, this would be a base64 QR code image
        expiresIn: 5 * 60, // 5 minutes in seconds
        paymentReference: 'IGNITA-' + Date.now()
      };
      
      setXamanPaymentData(mockPaymentData);
      onOpen(); // Open payment modal
      
      // Simulate receiving payment
      // In a real app, you would poll your backend or use WebSocket for payment status
      setTimeout(() => {
        handlePaymentSuccess();
      }, 5000);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to initiate payment',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handlePaymentSuccess = () => {
    const totalCredits = getTotalCredits();
    
    // Add credits to user account
    addCredits(totalCredits);
    
    // Close the modal
    onClose();
    
    // Show success message
    toast({
      title: 'Payment successful',
      description: `${totalCredits} credits have been added to your account!`,
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    
    // Reset selected package
    setSelectedPackage(null);
  };
  
  return (
    <Container maxW="4xl" py={8}>
      <ScaleFade in={isVisible} initialScale={0.95}>
        <VStack spacing={8} align="stretch">
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <Box>
              <Flex align="center" mb={2}>
                <Image 
                  src="/IgnitaLogo.svg" 
                  alt="Ignita Logo" 
                  height="40px"
                  mr={3}
                />
                <Heading 
                  as="h1" 
                  size="xl" 
                  bgGradient={GRADIENTS.primary}
                  bgClip="text"
                  className="glow-text"
                  letterSpacing="tight"
                  fontFamily="Space Grotesk"
                >
                  Buy Credits
                </Heading>
              </Flex>
              <Text color={COLORS.textSecondary}>
                Purchase credits using XGCL to generate AI images, videos, and chat with our AI
              </Text>
            </Box>
            
            <Badge 
              px={4} 
              py={2} 
              borderRadius="md" 
              fontWeight="bold"
              bg="rgba(0, 168, 107, 0.15)"
              border="1px solid"
              borderColor={COLORS.primary}
              color={COLORS.accent}
              className="pulse"
            >
              Current Balance: {balance} Credits
            </Badge>
          </Flex>
          
          {/* Current Exchange Rate - Hidden for now
          <Box 
            bg="rgba(17, 18, 20, 0.7)" 
            p={5} 
            borderRadius="lg"
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
              <Box>
                <Heading size="md" className="glow-text" mb={1}>Current Exchange Rate</Heading>
                <Text color={COLORS.textSecondary}>
                  Best Value: 3.75 AI Credits = 1 XGCL (Saver Pack)
                </Text>
              </Box>
              
              <Stat textAlign="right">
                <StatLabel color={COLORS.textSecondary}>XGCL Price</StatLabel>
                <StatNumber color={COLORS.text}>$0.25 USD</StatNumber>
              </Stat>
            </Flex>
          </Box>
          */}
          
          {/* Credit Usage Information */}
          <Box 
            bg="rgba(17, 18, 20, 0.7)" 
            p={5} 
            borderRadius="lg"
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
            mb={4}
          >
            <Heading size="md" className="glow-text" mb={3}>Credit Usage Guide</Heading>
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
              <Box>
                <HStack mb={2}>
                  <Icon as={FaImage} color={COLORS.primary} boxSize={5} />
                  <Heading size="sm" color={COLORS.text}>Image Generation</Heading>
                </HStack>
                <Text color={COLORS.textSecondary} fontSize="sm">
                  • Standard: 1 credit
                </Text>
                <Text color={COLORS.textSecondary} fontSize="sm">
                  • HD: 2 credits
                </Text>
                <Text color={COLORS.textSecondary} fontSize="sm">
                  • Ultra HD: 3 credits
                </Text>
              </Box>
              
              <Box>
                <HStack mb={2}>
                  <Icon as={FaVideo} color={COLORS.primary} boxSize={5} />
                  <Heading size="sm" color={COLORS.text}>Video Generation</Heading>
                </HStack>
                <Text color={COLORS.textSecondary} fontSize="sm">
                  • Standard: 5 credits
                </Text>
                <Text color={COLORS.textSecondary} fontSize="sm">
                  • HD: 10 credits
                </Text>
                <Text color={COLORS.textSecondary} fontSize="sm">
                  • Longer durations: additional credits
                </Text>
              </Box>
              
              <Box>
                <HStack mb={2}>
                  <Icon as={FaRobot} color={COLORS.primary} boxSize={5} />
                  <Heading size="sm" color={COLORS.text}>AI Chat</Heading>
                </HStack>
                <Text color={COLORS.textSecondary} fontSize="sm">
                  • 0.2 credits per message
                </Text>
                <Text color={COLORS.textSecondary} fontSize="sm">
                  • Based on message length
                </Text>
                <Text color={COLORS.textSecondary} fontSize="sm">
                  • Advanced responses cost more
                </Text>
              </Box>
            </Grid>
          </Box>
          
          {/* Credit Packages */}
          <Box>
            <Heading size="md" mb={4} className="glow-text">Select Credit Package</Heading>
            
            <Grid 
              templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
              gap={6}
              mb={6}
            >
              {creditPackages.map((pkg) => (
                <CreditPackage
                  key={pkg.id}
                  amount={pkg.amount}
                  price={pkg.price}
                  bonusPercentage={pkg.bonusPercentage}
                  label={pkg.label}
                  isSelected={selectedPackage?.id === pkg.id}
                  onSelect={() => handleSelectPackage(pkg)}
                />
              ))}
            </Grid>
          </Box>
          
          {/* Pricing Summary */}
          <Box 
            bg="rgba(17, 18, 20, 0.7)" 
            p={5} 
            borderRadius="lg"
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Heading size="md" mb={4} className="glow-text">Summary</Heading>
            
            <Flex justify="space-between" mb={2}>
              <Text color={COLORS.textSecondary}>Total Credits:</Text>
              <Text color={COLORS.text} fontWeight="bold">{getTotalCredits()}</Text>
            </Flex>
            
            <Flex justify="space-between" mb={4}>
              <Text color={COLORS.textSecondary}>Price in XGCL:</Text>
              <Text color={COLORS.text} fontWeight="bold">{getXgcPrice().toLocaleString()} XGCL</Text>
            </Flex>
            
            <Button
              onClick={handlePurchase}
              isLoading={isProcessing}
              loadingText="Processing..."
              isDisabled={!selectedPackage}
              width="100%"
              size="lg"
              className="primary-button"
            >
              Purchase with XGCL
            </Button>
            
            <Text fontSize="xs" textAlign="center" mt={2} color={COLORS.textSecondary}>
              Secure payment processed via XAMAN wallet
            </Text>
          </Box>
        </VStack>
        
        {/* Payment Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
          <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.700" />
          <ModalContent bg={COLORS.background} borderColor={COLORS.primary} borderWidth="1px">
            <ModalHeader color={COLORS.text}>Complete Payment</ModalHeader>
            <ModalCloseButton color={COLORS.text} />
            
            <ModalBody pb={6}>
              <VStack spacing={4} align="stretch">
                <Box textAlign="center" py={2}>
                  <Text color={COLORS.textSecondary} mb={4}>
                    Scan this QR code with your XAMAN wallet or click the button below to open XAMAN
                  </Text>
                  
                  {/* QR Code placeholder - in a real app, display the actual QR */}
                  <Box 
                    width="200px" 
                    height="200px" 
                    bg="white" 
                    margin="0 auto" 
                    border="1px solid"
                    borderColor={COLORS.primary}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    className="data-flow"
                    position="relative"
                  >
                    <Text color="black">QR Code Placeholder</Text>
                    <Box 
                      position="absolute"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      borderRadius="md"
                      bg="rgba(30, 220, 11, 0.1)"
                      pointerEvents="none"
                    />
                  </Box>
                </Box>
                
                <Button
                  className="outline-button"
                  color={"black"}
                  onClick={() => window.open('https://xaman.app', '_blank')}
                >
                  Open in XAMAN Wallet
                </Button>
                
                <Divider borderColor="rgba(30, 220, 11, 0.3)" />
                
                <Box textAlign="center">
                  <Text color={COLORS.textSecondary}>
                    Waiting for payment confirmation...
                  </Text>
                  <HStack justify="center" mt={2}>
                    <span className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                  </HStack>
                </Box>
                
                <Text fontSize="xs" color={COLORS.textSecondary} textAlign="center">
                  Payment will be automatically detected when completed.
                  Do not close this window.
                </Text>
              </VStack>
            </ModalBody>
            
            <ModalFooter>
              <Button onClick={onClose} variant="ghost" color={COLORS.textSecondary}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </ScaleFade>
    </Container>
  );
}

export default BuyCredits; 